import * as React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlay, faTrash, faPause } from '@fortawesome/pro-solid-svg-icons'
import { styled } from 'styled-components'
import { O, pipe } from '@packages/prelude'
import { ControlButton } from '~/components/Buttons'

interface AudioPlayerProps {
  src: string
  onDiscard: () => void
}

const PreviewContainer = styled.div`
  display: flex;
  flex: 1;
  gap: 0.5rem;

  height: 100%;
  justify-content: flex-end;

  > ${ControlButton} {
    font-size: 1.25rem;
    padding: 0.5rem !important;

    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }
`

const PlayButtonLarge = styled(ControlButton)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  font-size: 3rem;
  margin-top: 0 !important;

  background-color: var(--bs-white) !important;
  color: var(--primary) !important;
  box-shadow: none !important;
`

const View: React.FC<AudioPlayerProps> = ({ src, onDiscard }) => {
  const [isPlaying, setIsPlaying] = React.useState(false)
  const audioRef = React.useRef<HTMLAudioElement>(null)

  React.useEffect(() => {
    pipe(
      audioRef.current,
      O.fromNullable,
      O.map(audio => (isPlaying ? audio.play() : audio.pause()))
    )
  }, [isPlaying])

  const onEnded = React.useCallback(() => {
    setIsPlaying(false)
    pipe(
      audioRef.current,
      O.fromNullable,
      O.map(audio => (audio.currentTime = 0))
    )
  }, [audioRef])

  const togglePlay = React.useCallback(() => {
    setIsPlaying(isPlaying => !isPlaying)
  }, [])

  const onTrash = React.useCallback(() => {
    setIsPlaying(false)
    onDiscard()
  }, [onDiscard])

  return (
    <>
      <audio src={src} ref={audioRef} onEnded={onEnded} autoPlay={false} />
      <PreviewContainer>
        <ControlButton onClick={onTrash}>
          <FontAwesomeIcon icon={faTrash} />
        </ControlButton>
        <ControlButton onClick={togglePlay}>
          <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
        </ControlButton>
      </PreviewContainer>
    </>
  )
}

const Simple: React.FC<Omit<AudioPlayerProps, 'onDiscard'>> = ({ src }) => {
  const [isPlaying, setIsPlaying] = React.useState(false)
  const audioRef = React.useRef<HTMLAudioElement>(null)

  React.useEffect(() => {
    if (audioRef.current !== null) {
      isPlaying ? audioRef.current.play() : audioRef.current.pause()
    }
  }, [isPlaying])

  const togglePlay = React.useCallback(() => {
    setIsPlaying(isPlaying => !isPlaying)
  }, [])

  return (
    <>
      <audio src={src} ref={audioRef} autoPlay={false} />
      <PlayButtonLarge onClick={togglePlay}>
        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
      </PlayButtonLarge>
    </>
  )
}

export default Object.assign(View, {
  Simple
})
